import { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import RegionProvinceDistrictFields from "../common/RegionSelectFields";
import { regionsData } from "../../constants/regiones"; 
import { Banner } from "../common/Banner";
import { industries, periodicities, statusCompanies, companySizes } from "../../constants/options";
import Swal from "sweetalert2";
import { editCompanie } from "../../services/companiesService";
import { useAuth } from "../../context/authContext";
const inputClass = "w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 outline-none shadow-sm";
const selectClass = "w-full border border-gray-300 p-3 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 outline-none appearance-none shadow-sm";
const labelClass = "block mb-2 font-medium text-gray-700";
const errorClass = "text-sm text-red-500 mt-1 font-medium";
const sectionClass = "p-6 bg-white rounded-xl shadow-lg border border-gray-200 transition-all duration-300 hover:shadow-xl";
const helperTextClass = "text-sm text-gray-500 mt-1";

const urlRegex = /^(https?:\/\/)?(www\.)?[\w\-]+\.[\w]{2,}(\/[\w\-]*)*$/;


// Función para formatear la fecha del formato DD-MM-YYYY a YYYY-MM-DD para el input date
const formatDateForInput = (dateString) => {
if (!dateString) return "";

// Si ya está en formato YYYY-MM-DD, devolverlo tal cual
if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;

// Convertir de DD-MM-YYYY a YYYY-MM-DD
const parts = dateString.split('-');
if (parts.length === 3) {
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
}
return dateString;
};


const validationSchema = Yup.object({
business_name: Yup.string().required("El campo de Razón Social es obligatorio."),
trade_name: Yup.string().required("El campo de Nombre Comercial es obligatorio."),
rut: Yup.string().matches(/^\d{7,8}-[\dkK]$/, "Formato inválido (Ej: 12345678-9)").required("El campo de RUT es obligatorio."),
contact_email: Yup.string().email("Correo inválido").required("El campo de Correo Electrónico es obligatorio."),
landline_phone: Yup.string().required("El campo de Teléfono Fijo es obligatorio."),
mobile_phone: Yup.string().required("El campo de Teléfono Móvil es obligatorio."),
address: Yup.string().required("El campo de Dirección es obligatorio."),
web_page: Yup.string().matches(urlRegex, "URL invalida").required("El campo de Página Web es obligatorio."),
region: Yup.string().required("El campo de Región es obligatorio."),
province: Yup.string().required("El campo de Provincia es obligatorio."),
district: Yup.string().required("El campo de Distrito es obligatorio."),
description: Yup.string().required("El campo de Descripción es obligatorio."),
industry: Yup.string().required("El campo de Industria es obligatorio."),
company_size: Yup.string().required("El campo de Tamaño de Empresa es obligatorio."),
status: Yup.string().required("El campo de Estado es obligatorio."),
entry_date: Yup.string().required("El campo de Fecha de Entrada es obligatorio."),
periodicity: Yup.string().required("El campo de Periodicidad es obligatorio."),
});

const Content = ({ detailsCompany, onUpdate, role, isSubmitting }) => {
  const { user } = useAuth();
const [isEditing, setIsEditing] = useState(false);
const [initialValues, setInitialValues] = useState({});
const [resetKey, setResetKey] = useState(0); // Clave para forzar el reinicio de Formik

const isAdmin = role === "admin";
const isSocio = role === "socio";
// Cancelar edición y restaurar valores originales
const handleCancel = () => {
  setIsEditing(false);
  setResetKey(prevKey => prevKey + 1); // Incrementar la clave para forzar el reinicio de Formik
};
// Prepara los valores iniciales, formateando la fecha
useEffect(() => {
  if (detailsCompany) {
    // Función para sanitizar valores: asegura que los strings nunca sean null o undefined
    const sanitizeString = (value) => {
      return value === null || value === undefined ? '' : String(value);
    };
    
    // Función para mapear valores a sus equivalentes en los options (para selects)
    const mapValueToOption = (value, options) => {
      if (!value) return '';
      
      // Si el valor es un valor real en los options, lo usamos
      const foundOption = options.find(opt => opt.value === value);
      if (foundOption) return value;
      
      // Si el valor es un label en los options, usamos su value
      const foundByLabel = options.find(opt => opt.label === value);
      if (foundByLabel) return foundByLabel.value;
      
      // Si no coincide, devolvemos el valor original como string
      return sanitizeString(value);
    };
    
    const formattedData = {
      // Sanitizar todos los campos de tipo string
      business_name: sanitizeString(detailsCompany.business_name),
      trade_name: sanitizeString(detailsCompany.trade_name),
      rut: sanitizeString(detailsCompany.rut),
      contact_email: sanitizeString(detailsCompany.contact_email),
      landline_phone: sanitizeString(detailsCompany.landline_phone),
      mobile_phone: sanitizeString(detailsCompany.mobile_phone),
      address: sanitizeString(detailsCompany.address),
      web_page: sanitizeString(detailsCompany.web_page),
      region: sanitizeString(detailsCompany.region),
      province: sanitizeString(detailsCompany.province),
      district: sanitizeString(detailsCompany.district),
      description: sanitizeString(detailsCompany.description),
      entry_date: formatDateForInput(detailsCompany.entry_date),
      // Mapear los valores actuales a los valores de options
      industry: mapValueToOption(detailsCompany.industry, industries),
      company_size: mapValueToOption(detailsCompany.company_size, companySizes),
      status: mapValueToOption(detailsCompany.status, statusCompanies),
      periodicity: mapValueToOption(detailsCompany.periodicity, periodicities)
    };
    setInitialValues(formattedData);
  }
}, [detailsCompany]);

const handleSubmit = async (values, { setSubmitting }) => {
    const confirmation = await Swal.fire({
      title: "¿Estás seguro?",
      text: "¿Quieres guardar los cambios?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, guardar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });
    
    if (!confirmation.isConfirmed) return;

    isSubmitting(true);
    try {
      const { password, role, ...dataToSend } = values;

      const response = await editCompanie(dataToSend, user);
      isSubmitting(false);
      if (!response.success) {
        Swal.fire("Error", response.message, "error");
      } else {
        Swal.fire("¡Guardado!", response.message, "success");
        setIsEditing(false);
        
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Ocurrió un error al guardar.", "error");
    } finally {
      setSubmitting(false);
      onUpdate();
    }
};

const canEditField = (field) => {
  if (!isEditing) return false;
  if (isAdmin) {
    return field !== "periodicity"; // Admin no ve periodicidad
  }
  if (isSocio) {
    return ["contact_email", "landline_phone", "mobile_phone", "address", "web_page", "description"].includes(field);
  }
  return false;
};

// Renderizar selects para ciertos campos
const renderField = (field, label, helperText) => {
  const isDisabled = !canEditField(field);
  const fieldClass = isDisabled 
    ? `${inputClass} bg-gray-100 cursor-not-allowed` 
    : inputClass;
  
  // Campos que deben ser selects
  if (field === "industry") {
    return (
      <Field
        as="select"
        name={field}
        id={field}
        className={isDisabled ? `${selectClass} bg-gray-100 cursor-not-allowed` : `${selectClass} bg-white`}
        disabled={isDisabled}
      >
        <option value="">Selecciona una industria</option>
        {industries.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </Field>
    );
  }
  
  if (field === "company_size") {
    return (
      <Field
        as="select"
        name={field}
        id={field}
        className={isDisabled ? `${selectClass} bg-gray-100 cursor-not-allowed` : `${selectClass} bg-white`}
        disabled={isDisabled}
      >
        <option value="">Selecciona un tamaño</option>
        {companySizes.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </Field>
    );
  }
  
  if (field === "status") {
    return (
      <Field
        as="select"
        name={field}
        id={field}
        className={isDisabled ? `${selectClass} bg-gray-100 cursor-not-allowed` : `${selectClass} bg-white`}
        disabled={isDisabled}
      >
        <option value="">Selecciona un estado</option>
        {statusCompanies.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </Field>
    );
  }
  
  if (field === "periodicity") {
    return (
      <Field
        as="select"
        name={field}
        id={field}
        className={isDisabled ? `${selectClass} bg-gray-100 cursor-not-allowed` : `${selectClass} bg-white`}
        disabled={isDisabled}
      >
        <option value="">Selecciona una periodicidad</option>
        {periodicities.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </Field>
    );
  }
  
  // Campo descripción como textarea
  if (field === "description") {
    return (
      <Field
        as="textarea"
        rows="4"
        name={field}
        id={field}
        className={fieldClass}
        disabled={isDisabled}
      />
    );
  }
  
  // Otros campos como inputs normales
  return (
    <Field
      type={field === "entry_date" ? "date" : "text"}
      name={field}
      id={field}
      className={fieldClass}
      disabled={isDisabled}
    />
  );
};

// Si aún no están disponibles los valores iniciales, muestra un indicador de carga
if (!Object.keys(initialValues).length) {
  return <div className="flex justify-center p-6">Cargando información...</div>;
}

return (
  <div className="max-w-full mx-auto">
    <Banner title="Información de la organización" message="Aquí puedes editar la información de tu organización."/>
    <div className={sectionClass}>
      {isEditing && (
        <div className="flex items-center bg-yellow-100 text-yellow-800 px-4 py-2 rounded-md shadow-md mb-4">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M12 18.5a6.5 6.5 0 100-13 6.5 6.5 0 000 13z"></path>
          </svg>
          <span>
            {isAdmin ? "Como administrador puedes editar todos los datos." : "Como socio solo puede editar datos de contacto, en caso de necesitar modificar datos sensibles, contacta a un administrador."}
          </span>
        </div>
      )}
      <Formik
        key={resetKey} // Usar la clave para forzar reinicio cuando cancela
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, dirty, isValid, errors }) => (
          <Form>
            {/* BOTÓN DE EDICIÓN */}
            <div className="flex justify-end mb-4">
              {!isEditing ? (
                <button 
                  type="button"
                  onClick={() => setIsEditing(true)} 
                  className="text-blue-600 hover:text-blue-800 font-medium flex items-center transition-colors border border-blue-600 rounded-md px-4 py-2"
                >
                  <span>Editar</span>
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536M16.732 3.732l-13.232 13.232v3.536h3.536l13.232-13.232"></path>
                  </svg>
                </button>
              ) : null}
            </div>

            {/* CAMPOS */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Campo genérico */}
              {[
                ["business_name", "Razón Social"],
                ["trade_name", "Nombre Comercial"],
                ["rut", "RUT", "El RUT debe tener el formato 12345678-9"],
                ["industry", "Industria", "Selecciona la industria"],
                ["description", "Descripción", "Descripción de la empresa"],
                ["entry_date", "Fecha de ingreso", "Formato: dd-mm-yyyy"],
                ["company_size", "Tamaño de la organización", "Selecciona el tamaño de la organización"],
                ["status", "Estado", "Selecciona el estado de la organización"],
                ["contact_email", "Correo de contacto"],
                ["landline_phone", "Teléfono fijo", "Formato: (51)-2 123 456"],
                ["mobile_phone", "Teléfono móvil", "Formato: +56 9 12345678"],
                ["address", "Dirección"],
                ["web_page", "Página web", "Ingresa una URL válida (www.ejemplo.cl o https://...)"],
                ["periodicity", "Periodicidad",  "Selecciona la periodicidad de la organización"],
              ]
                .filter(([field]) => !(isAdmin && field === "periodicity")) // ocultar para admin
                .map(([field, label, helperText]) => {
                  // El campo de descripción debería ocupar todo el ancho
                  const colSpan = field === "description" ? "col-span-1 md:col-span-2" : "";
                  
                  return (
                    <div key={field} className={colSpan}>
                      <label htmlFor={field} className={labelClass}>
                        {label} <span className="text-red-500">*</span>
                      </label>
                      {renderField(field, label, helperText)}
                      {isEditing && helperText && <p className={helperTextClass}>{helperText}</p>}
                      <ErrorMessage name={field} component="div" className={errorClass} />
                    </div>
                  );
                })}

              {/* Región, Provincia, Comuna */}
              <RegionProvinceDistrictFields
                regions={regionsData?.Regions || []}
                styles={{ selectClass, labelClass, errorClass }}
                showLabels={true}
                helperTexts={{
                  region: "Seleccione la región",
                  province: "Seleccione la provincia",
                  district: "Seleccione la comuna"
                }}
                disabled={!isEditing || user.role !== "admin"}
              />
            </div>
            
            {!isValid && Object.keys(errors).length > 0 && (
              <div className="mt-4 p-4 bg-red-50 border border-red-300 rounded-lg">
                <h4 className="font-semibold text-red-700 mb-2">Faltan campos o hay errores:</h4>
                <ul className="list-disc list-inside text-red-600 text-sm">
                  {Object.entries(errors).map(([field, errorMsg]) => (
                    <li key={field}>{errorMsg}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* BOTONES DE ACCIÓN */}
            {isEditing && (
              <div className="mt-8 flex justify-end gap-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !(dirty && isValid)}
                  className={`
                    px-4 py-2 rounded-md text-white text-sm font-medium transition-colors
                    ${(dirty && isValid)
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-blue-300 cursor-not-allowed"}
                  `}
                >
                  Guardar
                </button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  </div>
);
};

export default Content;